import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { hideWindAlarmWindow } from "../../redux/windAlarmWindow";
import { useChartSeriesVisibility } from "../../hooks/useChartSeriesVisibility";
import { useActiveWindAlarmScenario } from "../CustomHooks";
import { generalNumbersFormatter } from "../utility/charts";
import {
  buildSimpleWindRoseChartData,
  getWindRoseSeriesColor,
} from "../utility/windAlarmWindows";
import InfoBoxDashboardContainer from "./InfoBoxDashboard";
import "./WindAlarmWindowInfoBox.css";

const formatValue = (value, suffix = "") => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const num = Number(value);
  const text = Number.isInteger(num)
    ? String(num)
    : String(Number(num.toFixed(3)));
  return `${text}${suffix}`;
};

const MetaRow = ({ label, value }) => (
  <div className="wa-meta-row">
    <span className="wa-meta-label">{label}</span>
    <span className="wa-meta-value">{value}</span>
  </div>
);

const RiskHoursRow = ({ label, value }) => (
  <div className="wa-risk-row">
    <span className="wa-risk-label">{label}</span>
    <span className="wa-risk-value">{formatValue(value)}</span>
  </div>
);

const WindAlarmWindowInfoBox = () => {
  const dispatch = useDispatch();
  const captureRef = useRef(null);

  const windAlarmActive = useSelector((state) =>
    Object.values(state.shadingSystem.windAlarmSensors || {}).some(Boolean),
  );
  const activeScenario = useActiveWindAlarmScenario();
  const { isShowing, selectedWindow } = useSelector(
    (state) => state.windAlarmWindow,
  );

  const { data, seriesNames } = buildSimpleWindRoseChartData(
    selectedWindow?.windRoseSeries,
  );

  const { hiddenSeries, visibleSeries, toggleSeries } = useChartSeriesVisibility(
    seriesNames,
    selectedWindow?.name || "",
  );

  const radiusMax = Math.max(
    2,
    ...data.flatMap((row) =>
      visibleSeries.map((name) => Number(row[name]) || 0),
    ),
    0.1,
  );

  if (!windAlarmActive || !isShowing || !selectedWindow) return null;

  return (
    <InfoBoxDashboardContainer
      handleClickOutside={() => dispatch(hideWindAlarmWindow())}
      needsCapture
      captureProps={{
        targetElement: () => captureRef?.current,
      }}
      style={{
        display: "block",
        width: "1020px",
        height: "500px",
      }}
    >
      <div ref={captureRef} className="wa-info-root">
        <div className="wa-info-chart">
          <p className="wa-section-title">Wind alarm rose</p>
          <p className="wa-chart-hint">Click a legend item to show / hide</p>
          <ResponsiveContainer width="100%" height="88%">
            <RadarChart outerRadius="75%" data={data}>
              <Legend
                wrapperStyle={{ cursor: "pointer" }}
                onClick={(entry) => {
                  if (entry?.dataKey) toggleSeries(entry.dataKey);
                }}
                formatter={(value) => (
                  <span
                    className={
                      hiddenSeries[value]
                        ? "wa-legend-item wa-legend-item--hidden"
                        : "wa-legend-item"
                    }
                  >
                    {value}
                  </span>
                )}
              />
              <PolarGrid />
              <PolarAngleAxis dataKey="direction" tick={{ fontSize: 12 }} />
              <PolarRadiusAxis
                domain={[0, radiusMax]}
                tickCount={7}
                tick={{ fontSize: 11, fontWeight: 500 }}
                angle={10}
              />
              <Tooltip formatter={generalNumbersFormatter} />
              {seriesNames.map((seriesName, index) => {
                const { stroke, fill } = getWindRoseSeriesColor(index);
                return (
                  <Radar
                    key={seriesName}
                    name={seriesName}
                    dataKey={seriesName}
                    stroke={stroke}
                    fill={fill}
                    fillOpacity={visibleSeries.length > 1 ? 0.25 : 0.55}
                    hide={!!hiddenSeries[seriesName]}
                  />
                );
              })}
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <aside className="wa-info-panel">
          <div className="wa-panel-header">
            <p className="wa-panel-kicker">Window details</p>
            <h3 className="wa-panel-title">{selectedWindow.name}</h3>
          </div>

          <div className="wa-meta-block">
            <MetaRow
              label="Scenario name"
              value={selectedWindow.scenario_name || activeScenario || "—"}
            />
            <MetaRow label="Window name" value={selectedWindow.name || "—"} />
            <MetaRow
              label="Wind group name"
              value={selectedWindow.wind_group_name || "—"}
            />
            <MetaRow
              label="Safety factor"
              value={formatValue(selectedWindow.safety_factor)}
            />
            <MetaRow
              label="Average cut off windspeed"
              value={formatValue(
                selectedWindow.wind_group_avg_cutoff_windspeed_ms,
                " m/s",
              )}
            />
          </div>

          <div className="wa-risk-block">
            <p className="wa-section-title">Risk hours</p>
            <RiskHoursRow
              label="Hours of Windalarm"
              value={selectedWindow.hours_windalarm_total}
            />
            <RiskHoursRow
              label="Hours of false Windalarm"
              value={selectedWindow.hours_alarm_without_risk}
            />
            <RiskHoursRow
              label="Hours of Windrisk"
              value={selectedWindow.hours_windrisk}
            />
            <RiskHoursRow
              label="Hours of missing Windalarm"
              value={selectedWindow.hours_risk_without_alarm}
            />
          </div>
        </aside>
      </div>
    </InfoBoxDashboardContainer>
  );
};

export default WindAlarmWindowInfoBox;
